import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  NodeKey,
  SerializedLexicalNode,
} from "lexical";
import * as React from "react";
import { JSX } from "react";

function ImageComponent({ src, altText }: { src: string; altText: string }) {
  return <img src={src} alt={altText} />;
}

function convertImageElement(domNode: Node): DOMConversionOutput | null {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src } = domNode;
    const node = $createImageNode(src, altText);
    return { node };
  }
  return null;
}

export interface SerializedImageNode extends SerializedLexicalNode {
  src: string;
  altText: string;
  type: "image";
  version: 1;
}

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    const clonedNode = new ImageNode(node.__key);
    clonedNode.__src = node.__src;
    clonedNode.__altText = node.__altText;
    return clonedNode;
  }

  constructor(key?: NodeKey) {
    // Constructor now takes 0 required arguments
    super(key);
    this.__src = ""; // Initialize with default empty string
    this.__altText = ""; // Initialize with default empty string
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const node = $createImageNode(serializedNode.src, serializedNode.altText); // Still use $createImageNode for consistency
    node.setSrc(serializedNode.src);
    node.setAltText(serializedNode.altText);
    return node;
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      src: this.getSrc(),
      type: "image",
      version: 1,
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("img");
    element.setAttribute("src", this.__src);
    element.setAttribute("alt", this.__altText);
    return { element };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement("div");
    div.style.display = "contents";
    return div;
  }

  updateDOM(): false {
    return false;
  }

  setSrc(src: string): void {
    const writable = this.getWritable();
    writable.__src = src;
  }

  getSrc(): string {
    return this.__src;
  }

  setAltText(altText: string): void {
    const writable = this.getWritable();
    writable.__altText = altText;
  }

  getAltText(): string {
    return this.__altText;
  }

  getTextContent(): string {
    return this.__altText;
  }

  decorate(): JSX.Element {
    return React.createElement(ImageComponent, {
      src: this.__src,
      altText: this.__altText,
    });
  }
}

export function $createImageNode(src: string, altText: string): ImageNode {
  const imageNode = new ImageNode();
  imageNode.setSrc(src);
  imageNode.setAltText(altText);
  return imageNode;
}

export function $isImageNode(node: ImageNode | null | undefined): boolean {
  return node instanceof ImageNode;
}
